import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import theme from './theme/theme.js';

// Pages
import { StartupPage } from './pages/StartupPage.jsx';
import { LoginPage } from './pages/LoginPage.jsx';
import { HomePage } from './pages/HomePage.jsx';
import { MenuPage } from './pages/MenuPage.jsx';
import { BlogPage } from './pages/BlogPage.jsx';
import { MenuManagePage } from './pages/MenuManagePage.jsx';
import { TablesPage } from './pages/TablesPage.jsx';
import { KitchenPage } from './pages/KitchenPage.jsx';
import { CustomerOrderPage } from './pages/CustomerOrderPage.jsx';

// Components
import { ProtectedRoute } from './components/common/ProtectedRoute.jsx';

// Minisite Pages (no auth required — customer-facing)
import { BillPage } from './minisite/pages/BillPage.jsx';
import { ReceiptPage } from './minisite/pages/ReceiptPage.jsx';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            {/* ═══ Minisite Routes (no auth — customer-facing) ═══ */}
            <Route path="/s/:sessionToken" element={<BillPage />} />
            <Route path="/pay/:sessionToken" element={<BillPage />} />
            <Route path="/receipt/:receiptId" element={<ReceiptPage />} />

            {/* ═══ Customer Ordering (no auth — QR code entry) ═══ */}
            <Route path="/cust/:slug/:tableNo" element={<CustomerOrderPage />} />

            {/* ═══ Main App Routes ═══ */}
            <Route path="/" element={<StartupPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/menu" element={<MenuPage />} />

            {/* ═══ Protected Routes (Role-based) ═══ */}
            <Route
              path="/menu-manage"
              element={
                <ProtectedRoute allowedRoles={['owner']}>
                  <MenuManagePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tables"
              element={
                <ProtectedRoute allowedRoles={['owner', 'staff']}>
                  <TablesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kitchen"
              element={
                <ProtectedRoute allowedRoles={['owner', 'staff']}>
                  <KitchenPage />
                </ProtectedRoute>
              }
            />

            {/* Legacy route — redirect to new home */}
            <Route path="/dashboard/*" element={<Navigate to="/home" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
